import { useMemo, useState } from "react";
import { BookOpen, ChevronDown, Info, Lightbulb, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { findTermByLabel, splitTextByFinancialTerms } from "@/lib/financialTerms";
import type { LessonVocabEntry } from "@/data/lessonVocabularyRegistry";

interface InlineFinancialTextProps {
  text: string;
  className?: string;
  onSelectTerm?: (label: string) => void;
}

export function InlineFinancialText({ text, className, onSelectTerm }: InlineFinancialTextProps) {
  const parts = useMemo(() => splitTextByFinancialTerms(text), [text]);

  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part.type === "text") {
          return <span key={`${part.value}-${index}`}>{part.value}</span>;
        }

        const term = findTermByLabel(part.label);
        if (!term) {
          return <span key={`${part.value}-${index}`}>{part.value}</span>;
        }

        return (
          <Tooltip key={`${part.value}-${index}`}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onSelectTerm?.(term.label)}
                className="inline rounded-sm border border-primary/30 bg-primary/10 px-1 align-baseline text-left text-primary underline-offset-2 hover:bg-primary/15 hover:underline"
              >
                {part.value}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[240px] text-xs leading-relaxed">
              {term.definition}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </span>
  );
}

interface FinancialTermSupportProps {
  /** Curated, lesson-specific vocabulary entries. If empty, the panel hides. */
  lessonTerms: LessonVocabEntry[];
  activeTerm: string | null;
  onSelectTerm: (label: string | null) => void;
  className?: string;
  /** Override the panel header label. */
  label?: string;
  /** Optional surface hint shown in the panel ("Watch for…"). */
  surfaceHint?: string;
}

/**
 * Premium concept-support panel. Only renders lesson-specific terms — never
 * a generic global glossary. If no curated terms exist for the lesson, the
 * whole panel is hidden.
 */
export default function FinancialTermSupport({
  lessonTerms,
  activeTerm,
  onSelectTerm,
  className,
  label = "Concept Support",
  surfaceHint,
}: FinancialTermSupportProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => lessonTerms.find((t) => t.term.toLowerCase() === (activeTerm ?? "").toLowerCase()) ?? null,
    [lessonTerms, activeTerm]
  );

  if (lessonTerms.length === 0) return null;

  return (
    <div className={cn("space-y-2", className)}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              "group relative flex w-full items-center justify-between overflow-hidden rounded-2xl px-4 py-3 text-left",
              "border border-cyan-400/25 bg-gradient-to-br from-cyan-500/10 via-card/40 to-violet-500/10",
              "backdrop-blur-xl shadow-[0_0_24px_-12px_hsl(192_90%_60%/0.45)] transition-all",
              "hover:border-cyan-400/45 hover:shadow-[0_0_32px_-10px_hsl(192_90%_60%/0.6)]"
            )}
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent" />
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-500/15 ring-1 ring-cyan-300/30">
                <BookOpen className="h-4 w-4 text-cyan-200" />
              </div>
              <div>
                <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.22em] text-cyan-100/90">
                  {label}
                </p>
                <p className="text-xs text-foreground/70">
                  {lessonTerms.length} {lessonTerms.length === 1 ? "term" : "terms"} from this lesson
                </p>
              </div>
            </div>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-cyan-200/70 transition-transform duration-300",
                open && "rotate-180"
              )}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="pt-2">
          <div
            className={cn(
              "relative space-y-3 overflow-hidden rounded-2xl p-3",
              "border border-cyan-400/20 bg-gradient-to-br from-card/60 via-card/40 to-violet-500/5",
              "backdrop-blur-xl shadow-[0_0_28px_-16px_hsl(265_80%_60%/0.45)]"
            )}
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/40 to-transparent" />

            <div className="flex flex-wrap gap-1.5">
              {lessonTerms.map((term) => {
                const isActive = selected?.id === term.id;
                return (
                  <button
                    key={term.id}
                    type="button"
                    onClick={() => onSelectTerm(isActive ? null : term.term)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all",
                      isActive
                        ? "border-cyan-300/60 bg-cyan-400/15 text-cyan-50 shadow-[0_0_14px_-6px_hsl(192_90%_60%/0.7)]"
                        : "border-border/60 bg-background/40 text-foreground/75 hover:border-cyan-300/40 hover:text-cyan-100"
                    )}
                  >
                    {term.term}
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              {selected ? (
                <motion.div
                  key={selected.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                  className="rounded-xl border border-cyan-300/20 bg-background/40 p-3 backdrop-blur-md"
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-[10px] font-heading font-semibold uppercase tracking-[0.18em] text-cyan-200">
                      {selected.term}
                    </span>
                    <span className="text-[10px] text-foreground/50">·</span>
                    <span className="text-[11px] text-foreground/70">{selected.shortDefinition}</span>
                  </div>

                  <div className="space-y-2 pt-1 text-xs leading-relaxed">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/55">In this lesson</p>
                      <p className="text-foreground/85">{selected.lessonSpecificDefinition}</p>
                    </div>
                    <div className="flex gap-2 rounded-lg border border-border/40 bg-background/30 px-2.5 py-1.5">
                      <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300/80" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/55">Why it matters</p>
                        <p className="text-foreground/80">{selected.whyItMatters}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/55">Example</p>
                      <p className="italic text-foreground/75">{selected.example}</p>
                    </div>
                    {surfaceHint && (
                      <div className="flex items-start gap-2 rounded-lg border border-cyan-300/20 bg-cyan-400/5 px-2.5 py-1.5">
                        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-200/90" />
                        <p className="text-[11px] text-cyan-50/90">{surfaceHint}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.p
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 px-1 pt-1 text-[11px] text-foreground/55"
                >
                  <Info className="h-3 w-3" /> Tap a term to see how it shows up in this lesson.
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
